/**
 * BackgroundRemovalTab Component
 *
 * Tab for removing background from images using AI mask models.
 * Supports multiple mask models including U2Net, ISNet, and SAM.
 */

import { useState } from 'react';
import { Eraser, Loader2, Download, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/useToast';
import { ImageUploader } from '../ImageUploader';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { MaskModel, RemoveBackgroundResponse } from '@/types';

import { handleError } from '@/utils/errorHandler';

interface MaskModelOption {
  value: MaskModel;
  label: string;
  description: string;
}

const MASK_MODELS: MaskModelOption[] = [
  {
    value: 'u2net',
    label: 'U2Net (General Purpose)',
    description: 'Best for general background removal',
  },
  {
    value: 'u2netp',
    label: 'U2Net Portable',
    description: 'Faster but slightly less accurate',
  },
  {
    value: 'u2net_human_seg',
    label: 'Human Segmentation',
    description: 'Optimized for people and portraits',
  },
  {
    value: 'u2net_cloth_seg',
    label: 'Clothing Segmentation',
    description: 'For separating clothing items',
  },
  {
    value: 'silueta',
    label: 'Silueta',
    description: 'Silhouette-based detection',
  },
  {
    value: 'isnet-general-use',
    label: 'ISNet (High Quality)',
    description: 'Higher quality, more detail preservation',
  },
  {
    value: 'isnet-anime',
    label: 'ISNet Anime',
    description: 'Optimized for anime/illustration',
  },
  {
    value: 'sam',
    label: 'Segment Anything (SAM)',
    description: 'Most flexible, works on any object',
  },
];

export function BackgroundRemovalTab() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [sourceImage, setSourceImage] = useState<string>('');
  const [maskModel, setMaskModel] = useState<MaskModel>('u2net');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<RemoveBackgroundResponse | null>(null);
  const [showMask, setShowMask] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const handleImageSelect = (base64: string) => {
    setSourceImage(base64);
    setResult(null);
    setShowMask(false);
    setShowOriginal(false);
  };

  const handleImageRemove = () => {
    setSourceImage('');
    setResult(null);
  };

  const handleRemoveBackground = async () => {
    if (!sourceImage) {
      showToast(
        'error',
        t('image.removeBackground.errors.noImage', { defaultValue: 'Error' }),
        t('image.removeBackground.errors.noImageMessage', {
          defaultValue: 'Please upload an image first',
        })
      );
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const response = await imageGenerationAPI.removeBackground({
        image: sourceImage,
        mask_model: maskModel,
        output_format: 'png',
      });

      if (response.success && response.data) {
        setResult(response.data);
        showToast(
          'success',
          t('image.removeBackground.success.title', { defaultValue: 'Background Removed!' }),
          t('image.removeBackground.success.message', {
            defaultValue: 'Processed in {{time}}s',
            time: response.data.processing_time_seconds?.toFixed(1) ?? '0',
          })
        );
      } else {
        showToast(
          'error',
          t('image.removeBackground.errors.failed', { defaultValue: 'Processing Failed' }),
          response.error || 'Unknown error'
        );
      }
    } catch (error) {
      const errorMessage = handleError(error, 'BackgroundRemovalTab', 'Unknown error');
      showToast('error', 'Error', errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result?.image) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${result.image}`;
    link.download = 'background-removed.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast(
      'success',
      t('image.removeBackground.download.title', { defaultValue: 'Downloaded!' }),
      ''
    );
  };

  const handleDownloadMask = () => {
    if (!result?.mask) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${result.mask}`;
    link.download = 'mask.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getDisplayImage = () => {
    if (showOriginal && sourceImage) {
      return sourceImage.startsWith('data:') ? sourceImage : `data:image/png;base64,${sourceImage}`;
    }
    if (showMask && result?.mask) {
      return `data:image/png;base64,${result.mask}`;
    }
    if (result?.image) {
      return `data:image/png;base64,${result.image}`;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-2">
        <Eraser className="h-5 w-5 text-primary-400 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t('image.removeBackground.title', { defaultValue: 'Remove Background' })}
          </h3>
          <p className="text-sm text-white/60">
            {t('image.removeBackground.description', {
              defaultValue: 'Automatically remove the background from your image using AI',
            })}
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="form-label">
          {t('image.removeBackground.uploadPrompt', { defaultValue: 'Upload an image' })}
        </label>
        <ImageUploader onImageSelect={handleImageSelect} onRemove={handleImageRemove} />
      </div>

      {/* Model Selection */}
      {sourceImage && (
        <div>
          <label className="form-label">
            {t('image.removeBackground.maskModel', { defaultValue: 'Detection Model' })}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {MASK_MODELS.map((model) => (
              <button
                key={model.value}
                onClick={() => setMaskModel(model.value)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  maskModel === model.value
                    ? 'border-primary-500 bg-primary-500/20 text-white'
                    : 'border-secondary-700 bg-black/20 text-white/80 hover:border-primary-500/40'
                }`}
              >
                <div className="font-medium text-sm">{model.label}</div>
                <div className="text-xs mt-0.5 opacity-75">{model.description}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Process Button */}
      {sourceImage && !result && (
        <button
          onClick={handleRemoveBackground}
          disabled={isProcessing}
          className="btn-primary-full"
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>
                {t('image.removeBackground.processing', { defaultValue: 'Removing background...' })}
              </span>
            </>
          ) : (
            <>
              <Eraser className="h-5 w-5" />
              <span>
                {t('image.removeBackground.process', { defaultValue: 'Remove Background' })}
              </span>
            </>
          )}
        </button>
      )}

      {/* Result Display */}
      {result && (
        <div className="space-y-4">
          {/* Result Preview */}
          <div className="relative">
            <div
              className="rounded-lg overflow-hidden bg-[url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAADRJREFUOI1j/P///38GKgImKmoYtYBmFoxaMJIsoJcFVHVBeAYw0sMFo84Y0UF4BjDQwwUAy/QHBV++TtQAAAAASUVORK5CYII=')]"
              style={{ backgroundSize: '20px 20px' }}
            >
              <img
                src={getDisplayImage() || ''}
                alt="Result"
                className="w-full h-auto object-contain"
                style={{ maxHeight: 'clamp(250px, 50vh, 500px)' }}
              />
            </div>

            {/* View Toggle Buttons */}
            <div className="absolute top-3 right-3 flex space-x-2">
              <button
                onClick={() => {
                  setShowOriginal(!showOriginal);
                  setShowMask(false);
                }}
                className={`p-2 rounded-lg transition-all ${
                  showOriginal
                    ? 'bg-primary-600 text-white'
                    : 'bg-black/60 text-white hover:bg-black/80'
                } shadow-md`}
                title={t('image.removeBackground.showOriginal', { defaultValue: 'Show Original' })}
              >
                {showOriginal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              {result.mask && (
                <button
                  onClick={() => {
                    setShowMask(!showMask);
                    setShowOriginal(false);
                  }}
                  className={`p-2 rounded-lg transition-all ${
                    showMask
                      ? 'bg-primary-600 text-white'
                      : 'bg-black/60 text-white hover:bg-black/80'
                  } shadow-md`}
                  title={t('image.removeBackground.showMask', { defaultValue: 'Show Mask' })}
                >
                  <div className="w-4 h-4 rounded-full bg-current opacity-50" />
                </button>
              )}
            </div>
          </div>

          {/* Processing Time */}
          {result.processing_time_seconds && (
            <div className="text-xs text-white/60 text-center">
              {t('image.removeBackground.processingTime', {
                defaultValue: 'Processed in {{time}} seconds',
                time: result.processing_time_seconds.toFixed(1),
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="flex-1 btn-primary flex items-center justify-center space-x-2"
            >
              <Download className="h-5 w-5" />
              <span>
                {t('image.removeBackground.downloadPng', { defaultValue: 'Download PNG' })}
              </span>
            </button>
            {result.mask && (
              <button
                onClick={handleDownloadMask}
                className="px-4 btn-secondary flex items-center justify-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span className="text-sm">
                  {t('image.removeBackground.downloadMask', { defaultValue: 'Mask' })}
                </span>
              </button>
            )}
            <button
              onClick={() => {
                setResult(null);
                setShowMask(false);
                setShowOriginal(false);
              }}
              className="px-4 btn-secondary flex items-center justify-center"
              title={t('image.removeBackground.tryAgain', { defaultValue: 'Try Again' })}
            >
              <RefreshCw className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BackgroundRemovalTab;
